import { useCallback, useState } from 'react';
import { Gap, P, Button } from '@salecycle/ui-component-library';
import { ControlCard } from 'sections/onsite-campaigns/pages/CampaignEditor/components/ControlCard';
import { DisplayWebContentThumbnail } from '../../DisplayWebContentThumbnail';
import styled from 'styled-components';
import { useTemplateSelector } from '../../state/selectors/useTemplateSelector';
import { useWorkflowForm } from '../../../../../contexts/CampaignWorkflowFormContext';
import { useTranslation } from 'react-i18next';
import { EmbeddedTemplateControls } from './EmbeddedTemplateControls';
import { OnsiteTemplateSelectorModal } from '../../../../OnsiteTemplateSelectorModal';

const ThumbnailTemplateArea = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.designToken.spacing1};
`;

export interface SelectTemplateControlProps {
  messageId: string;
}

export const SelectTemplateControl = ({ messageId }: SelectTemplateControlProps) => {
  const { t } = useTranslation('campaigns');
  const { formDisabled } = useWorkflowForm();

  const template = useTemplateSelector({ messageId });
  const isTemplateSelected = !!(template?.id || template?.name);

  const [isTemplateSelectorModalOpen, setIsTemplateSelectorModalOpen] = useState(false);
  const toggleTemplateSelectorModal = useCallback(() => setIsTemplateSelectorModalOpen(!isTemplateSelectorModalOpen), [
    isTemplateSelectorModalOpen,
  ]);

  return (
    <>
      <ControlCard>
        <span>{t('onsite.editor.workflow.displayWebContentMessage.selectTemplateControl.label')}</span>
        {!isTemplateSelected && (
          <>
            <Gap bottom={16} />
            <Button
              variation="primary"
              onClick={toggleTemplateSelectorModal}
              disabled={formDisabled}
              aria-label={t('onsite.editor.workflow.displayWebContentMessage.selectTemplateControl.selectTemplateAria')}
            >
              {t('onsite.editor.workflow.displayWebContentMessage.selectTemplateControl.selectTemplate')}
            </Button>
          </>
        )}
        {isTemplateSelected && (
          <>
            <Gap bottom={16} />
            <ThumbnailTemplateArea>
              <div>
                <P>{template?.name}</P>
                <Gap bottom={8} />
                <Button
                  variation="secondary"
                  onClick={toggleTemplateSelectorModal}
                  aria-label={t(
                    'onsite.editor.workflow.displayWebContentMessage.selectTemplateControl.changeTemplateAria',
                  )}
                >
                  {t('onsite.editor.workflow.displayWebContentMessage.selectTemplateControl.changeTemplate')}
                </Button>
              </div>
              <DisplayWebContentThumbnail id={messageId} />
            </ThumbnailTemplateArea>
          </>
        )}
        <Gap top={16} />
        <EmbeddedTemplateControls id={messageId} />
      </ControlCard>

      <OnsiteTemplateSelectorModal
        messageId={messageId}
        location="displayWebContent"
        onClose={toggleTemplateSelectorModal}
        open={isTemplateSelectorModalOpen}
      />
    </>
  );
};
